import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/apiClient";

export default function ResetPasswordPage() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (pw.length < 6) {
      setErr("Password must be at least 6 characters.");
      return;
    }
    if (pw !== confirm) {
      setErr("Passwords do not match.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
      setTimeout(() => nav("/login"), 2200);
    } catch (ex) {
      setErr(formatApiErrorDetail(ex.response?.data?.detail) || ex.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-6 py-12">
      <div className="w-full max-w-md bg-white border border-slate-200 rounded-2xl shadow-sm p-8 fade-up">
        <div className="mb-6">
          <div className="text-xs uppercase tracking-[0.25em] text-slate-500 mb-2 font-semibold">
            Password reset
          </div>
          <h2 className="font-display text-3xl font-black tracking-tighter">
            {done ? "All set." : "Set a new password."}
          </h2>
          {!done && (
            <p className="text-sm text-slate-600 mt-2">
              Choose a new password — at least 6 characters.
            </p>
          )}
          {done && (
            <p className="text-sm text-emerald-700 mt-2">
              Password updated. Redirecting you to sign in…
            </p>
          )}
        </div>

        {!token && !done && (
          <div className="border border-red-200 bg-red-50 text-red-700 text-sm px-3 py-2 mb-4 rounded-lg" data-testid="reset-no-token">
            This page requires a valid reset link. Please request a new one.
          </div>
        )}

        {!done && token && (
          <form onSubmit={submit} data-testid="reset-password-form">
            <div className="mb-4">
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1 block">
                New password
              </label>
              <input
                type="password"
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                required
                minLength={6}
                className="qb-input font-mono text-sm"
                data-testid="reset-pw-input"
                autoFocus
              />
            </div>
            <div className="mb-4">
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1 block">
                Confirm new password
              </label>
              <input
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
                minLength={6}
                className="qb-input font-mono text-sm"
                data-testid="reset-pw-confirm"
              />
            </div>

            {err && (
              <div className="border border-red-200 bg-red-50 text-red-700 text-sm px-3 py-2 mb-4 rounded-lg" data-testid="reset-error">
                {err}
              </div>
            )}

            <button
              disabled={busy}
              type="submit"
              className="w-full h-11 bg-brand-blue text-white font-semibold uppercase tracking-wider text-sm hover:bg-brand-blue-dark disabled:opacity-60 rounded-full transition-colors"
              data-testid="reset-submit-button"
            >
              {busy ? "Updating…" : "Update password"}
            </button>
          </form>
        )}

        <button
          type="button"
          onClick={() => nav("/login")}
          className="mt-4 text-xs uppercase tracking-wider text-slate-500 hover:text-brand-blue w-full font-semibold"
          data-testid="reset-back-to-login"
        >
          ← Back to sign in
        </button>
      </div>
    </div>
  );
}
